'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import type { Device } from '@mv/core/model';
import type { LexicalContent } from '@mv/core/model/heim/richText';
import type { InlineNotificationProps } from '@mv/web-ui';
import { Form, InlineNotification } from '@mv/web-ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import useCore from '@/modules/core/useCore';
import { useI18n } from '@/modules/i18n/useI18n';
import { useDataTags } from '@/modules/tracking/analytics/useDataTags';
import RichText from '@/payload/components/RichText/RichText';
import type { DeviceCheckBlock as Props } from '@/payload/types';
import styles from './DeviceCheck.module.scss';

const OTHER_MODEL_VALUE = 'other';

const deviceSchema = z.object({
	brand: z.string(),
	model: z.string(),
});

type DeviceFormData = z.infer<typeof deviceSchema>;

type DeviceNotification = {
	type: InlineNotificationProps['type'];
	message: string | LexicalContent;
};

const DeviceCheck = ({ blockType, id }: Props) => {
	const { blockRef } = useDataTags(blockType, id);
	const { heimClient } = useCore();
	const { terms } = useI18n();

	const {
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<DeviceFormData>({
		resolver: zodResolver(deviceSchema),
		defaultValues: {
			brand: '',
			model: '',
		},
	});

	const { data: devices = [], isLoading } = useQuery<Device[]>({
		queryKey: ['devices-esim'],
		queryFn: () => heimClient.getESimCompatibleDevices(),
	});

	const brands = [...new Set(devices.map((device) => device.brand))];

	const selectedBrand = watch('brand');
	const selectedModel = watch('model');

	useEffect(() => {
		if (devices.length > 0) {
			const userAgent = navigator.userAgent;
			const matchedDevice = devices.find(
				(device) => device.userAgent && new RegExp(device.userAgent, 'i').test(userAgent),
			);

			if (matchedDevice) {
				setValue('brand', matchedDevice.brand);
				setValue('model', matchedDevice.name);
			}
		}
	}, [devices, setValue]);

	const isDeviceCompatible = (device: Device | null): boolean => {
		return device?.simTypes?.includes('embedded') ?? false;
	};

	const getNotification = (): DeviceNotification | null => {
		if (!selectedModel) return null;

		if (selectedModel === OTHER_MODEL_VALUE) {
			return {
				type: 'danger',
				message: terms.eSimErrorMessage,
			};
		}

		const matchedDevice = devices.find((device) => device.name === selectedModel);

		if (!matchedDevice) return null;

		if (matchedDevice.note) {
			return {
				type: 'warning',
				message: matchedDevice.note,
			};
		}

		if (isDeviceCompatible(matchedDevice)) {
			return {
				type: 'success',
				message: terms.eSimSuccessMessage,
			};
		}

		return {
			type: 'danger',
			message: terms.eSimErrorMessage,
		};
	};

	const filteredModels = devices.filter((device) => device.brand === selectedBrand).map((device) => device.name);

	const notification = getNotification();

	return (
		<Form.Root
			data-component="DeviceCheckBlock"
			errors={errors}
			control={control}
			className={cx(styles.container, 'styleLevel1')}
			ref={blockRef}
		>
			<Form.Field name="brand">
				<Form.Label>{terms.brandLabel}</Form.Label>
				<Form.Select disabled={isLoading || brands.length === 0}>
					<Form.Select.Option value="" />
					{brands.map((brand) => (
						<Form.Select.Option key={brand} value={brand}>
							{brand}
						</Form.Select.Option>
					))}
				</Form.Select>
			</Form.Field>

			<Form.Field name="model">
				<Form.Label>{terms.modelLabel}</Form.Label>
				<Form.Select disabled={isLoading || !selectedBrand}>
					<Form.Select.Option value="" />
					{filteredModels.map((model) => (
						<Form.Select.Option key={model} value={model}>
							{model}
						</Form.Select.Option>
					))}
					<Form.Select.Option key={OTHER_MODEL_VALUE} value={OTHER_MODEL_VALUE}>
						{terms.other}
					</Form.Select.Option>
				</Form.Select>
			</Form.Field>

			{notification && (
				<InlineNotification type={notification.type} isDismissible={false} dismissAriaLabel={terms.dismissNotification}>
					{typeof notification.message === 'string' ? (
						notification.message
					) : (
						<RichText content={notification.message} />
					)}
				</InlineNotification>
			)}
		</Form.Root>
	);
};

export default DeviceCheck;
